import { apiFetch } from "./client";
import { registerProductList, registerProductSlug } from "../utils/productSlug";

export async function fetchProducts(collectionName, sortBy) {
  const qs = new URLSearchParams();
  if (collectionName) qs.set("CollectionName", collectionName);
  if (sortBy) qs.set("SortBy", sortBy);
  const query = qs.toString();
  const data = await apiFetch(`/api/product${query ? `?${query}` : ""}`, { token: null });
  if (Array.isArray(data)) {
    registerProductList(data);
  }
  return data;
}

export async function fetchProductById(id) {
  const data = await apiFetch(`/api/product/${encodeURIComponent(id)}`, { token: null });
  if (data) {
    registerProductSlug(data);
  }
  return data;
}

let collectionsPromise = null;

export function fetchCollections() {
  if (!collectionsPromise) {
    collectionsPromise = apiFetch(`/api/collection/`, { token: null })
      .catch((err) => {
        collectionsPromise = null;
        throw err;
      })
      .finally(() => {
        setTimeout(() => {
          collectionsPromise = null;
        }, 5000);
      });
  }
  return collectionsPromise;
}

export async function fetchBestSellingProducts(limit = 8, days = 30) {
  const qs = new URLSearchParams();
  if (limit) qs.set("limit", limit);
  if (days) qs.set("days", days);
  const query = qs.toString();
  const data = await apiFetch(`/api/product/best-sellers${query ? `?${query}` : ""}`, { token: null });
  if (Array.isArray(data)) {
    registerProductList(data);
  }
  return data;
}

let productCategoriesMapPromise = null;

/**
 * Fetches all enabled collections and maps each product ID to its collection info and displayOrder.
 * Results are cached in-memory for 60 seconds.
 */
export function fetchProductCategoriesMap() {
  if (!productCategoriesMapPromise) {
    productCategoriesMapPromise = (async () => {
      try {
        const collections = await fetchCollections();
        if (!Array.isArray(collections) || collections.length === 0) {
          return new Map();
        }

        // Fetch products for all collections in parallel
        const results = await Promise.all(
          collections.map(async (col) => {
            try {
              const products = await fetchProducts(col.name);
              return {
                collection: col,
                products: Array.isArray(products) ? products : [],
              };
            } catch (err) {
              console.warn(`Failed to fetch products for collection "${col.name}":`, err);
              return { collection: col, products: [] };
            }
          })
        );

        const map = new Map();
        for (const { collection, products } of results) {
          const displayOrder =
            typeof collection.displayOrder === "number"
              ? collection.displayOrder
              : Number(collection.displayOrder) || 999;

          for (const prod of products) {
            if (!prod?.id) continue;
            const prodId = String(prod.id).trim().toLowerCase();
            const existing = map.get(prodId);
            // If product already in map from another collection, keep the one with lowest displayOrder
            if (!existing || displayOrder < existing.displayOrder) {
              map.set(prodId, {
                displayOrder,
                categoryName: collection.name || "",
                categoryArabicName: collection.arabicName || "",
                categoryId: collection.id,
              });
            }
          }
        }
        return map;
      } catch (err) {
        console.error("Failed to build product categories map:", err);
        return new Map();
      }
    })().finally(() => {
      setTimeout(() => {
        productCategoriesMapPromise = null;
      }, 60000);
    });
  }
  return productCategoriesMapPromise;
}

